import {
  cartApi,
  getTranslation,
  openQuickshop,
  readKlaviyoLearnqProduct,
  reinitSwymActionButtons,
  trackKlaviyoAddedToCart,
  updateCart,
} from '../../helpers';
import ProductSwatches from './ProductSwatches';

const selectors = {
  quickShopTrigger: '[data-quickshop-trigger]',
  quickBuySize: '[data-quick-buy-size]',
  modalProduct: '[data-product]',
  modalContent: 'quickshop-content',
  swatchInput: '[data-product-item-swatch]',
  swatchesContainer: '[data-product-item-swatches]',
  productLinks: '[data-product-item-link]',
  imageLink: '[data-product-item-image-link]',
  titleLink: '[data-product-item-title-link]',
  price: '[data-product-item-price]',
  badges: '[data-product-item-badges]',
  color: '[data-product-item-color]',
  options: '[data-product-item-options]',
  addToCartForm: '[data-product-item-form]',
  addToCartButton: '[data-product-item-add-to-cart]',
  variantSelect: '[data-product-item-variant-select]',
  optionSelect: '[data-product-item-option-select]',
  cupSelect: '[data-product-item-cup-select]',
  lengthSelect: '[data-product-item-length-select]',
  sizeSelect: '[data-product-item-size-select]',
};

const FRAGMENT_SECTION_IDS = {
  default: 'card-fragment',
  small: 'card-fragment-small',
  flatlay: 'card-fragment-feed-flatlay',
  horizontal: 'card-fragment-horizontal',
  horizontalLarge: 'card-fragment-horizontal-large',
};

const productCardCache = new Map();

/**
 * A single product item in a collection / list of products
 *
 * @export
 * @class ProductItem
 */
export default class ProductItem {
  /**
   * Creates an instance of ProductItem.
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;
    this._activeSwapToken = 0;
    this._layout = this.el.dataset.productItemLayout || 'default';
    this._flatlay = this.el.dataset.productItemFlatlay === 'true';

    this._initQuickBuy();
    this._initQuickShop();
    this._initAddToCart();
    this._initSwatchSwap();
    this._initSwatchCarousel();
    reinitSwymActionButtons(this.el);
  }

  _getFragmentSectionId() {
    if (this._layout === 'horizontal-large') {
      return FRAGMENT_SECTION_IDS.horizontalLarge;
    }
    if (this._layout === 'horizontal') {
      return FRAGMENT_SECTION_IDS.horizontal;
    }
    if (this._flatlay) {
      return FRAGMENT_SECTION_IDS.flatlay;
    }
    if (this._layout === 'small') {
      return FRAGMENT_SECTION_IDS.small;
    }
    return FRAGMENT_SECTION_IDS.default;
  }

  _getCacheKey(handle) {
    const flatlayKey = this._flatlay ? 'flatlay' : 'default';
    return `${this._layout}:${flatlayKey}:${handle}`;
  }

  /**
   * Bind add-to-cart form to submit via AJAX
   */
  _initAddToCart() {
    const form = this.el.querySelector(selectors.addToCartForm);
    if (!form) {
      return;
    }

    form.addEventListener('submit', this._onAddToCart);
    this._initVariantOptionSync(form);
  }

  /**
   * When visible option selects change, sync the hidden variant id and button state
   */
  _initVariantOptionSync(form) {
    const variantSelect = form.querySelector(selectors.variantSelect);
    const optionSelects = form.querySelectorAll(selectors.optionSelect);
    const addButton = form.querySelector(selectors.addToCartButton);

    if (!variantSelect || !optionSelects.length) {
      return;
    }

    const syncVariant = () => {
      const selectedByPosition = {};
      let hasEmptySelection = false;

      optionSelects.forEach(select => {
        const pos = select.dataset.optionPosition;
        selectedByPosition[pos] = select.value;
        if (!select.value) {
          hasEmptySelection = true;
        }
      });

      const setAddButtonLabel = label => {
        if (!addButton) {
          return;
        }

        if (
          this._layout === 'horizontal' &&
          label === getTranslation('products.add_to_cart')
        ) {
          addButton.innerHTML = `<span class="xs:hidden">${getTranslation(
            'mini_cart.add_to_cart',
          )}</span><span class="max-xs:hidden">${label}</span>`;
          return;
        }

        addButton.textContent = label;
      };

      if (hasEmptySelection) {
        variantSelect.value = '';
        if (addButton) {
          addButton.disabled = true;
          setAddButtonLabel(getTranslation('products.add_to_cart'));
        }
        return;
      }

      const variantOptions = [...variantSelect.options].filter(
        opt => opt.value,
      );
      const matching = variantOptions.find(opt => {
        for (const pos in selectedByPosition) {
          const attr = `data-option${pos}`;
          if (opt.getAttribute(attr) !== selectedByPosition[pos]) {
            return false;
          }
        }
        return true;
      });

      if (matching) {
        variantSelect.value = matching.value;
        const isUnavailable = matching.hasAttribute('data-unavailable');
        if (addButton) {
          addButton.disabled = isUnavailable;
          setAddButtonLabel(
            isUnavailable
              ? getTranslation('products.sold_out')
              : getTranslation('products.add_to_cart'),
          );
        }
      }
    };

    optionSelects.forEach(select => {
      select.addEventListener('change', syncVariant);
    });
    syncVariant();
  }

  _onAddToCart = e => {
    e.preventDefault();

    const form = e.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }
    const finalSale = this.el.dataset.finalSale === 'true';
    const preorder = this.el.dataset.preorder !== undefined;
    const preorderText = this.el.dataset.preorder;

    const formData = new FormData(form);
    const data = cartApi.formatFormData(formData, {
      finalSale,
      preorder,
      preorderText,
    });

    const button = form.querySelector(selectors.addToCartButton);
    const originalText = button?.textContent;

    if (button) {
      button.disabled = true;
      button.textContent = getTranslation('products.adding');
    }

    cartApi
      .add({ items: [data] })
      .then(response => {
        if (response.sections) {
          updateCart({
            sections: response.sections,
            openCart: this._layout !== 'horizontal-large',
          });
        }
        trackKlaviyoAddedToCart(readKlaviyoLearnqProduct(this.el));
        if (button) {
          button.textContent = getTranslation('products.added');
          if (this._layout === 'horizontal-large') {
            // Keep the "Added" state until the user changes a variant option
            // (size select resets via syncVariant; colour/cup/length swaps
            // replace the form entirely, which resets the button).
            button.disabled = false;
          } else {
            setTimeout(() => {
              button.disabled = false;
              button.textContent = originalText;
            }, 1500);
          }
        }
      })
      .catch(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      });
  };

  /**
   * Bind Quick Shop trigger to open quickshop modal
   */
  _initQuickShop() {
    this.el.addEventListener('click', this._onQuickShopClick);
  }

  _onQuickShopClick = e => {
    const trigger = e.target.closest(selectors.quickShopTrigger);
    if (!trigger) {
      return;
    }

    const productUrl = trigger.dataset.productUrl ?? this.el.dataset.productUrl;
    if (productUrl) {
      e.preventDefault();
      openQuickshop(productUrl);
    }
  };

  /**
   * Bind Quick Buy size buttons to add variant to cart
   */
  _initQuickBuy() {
    this.el.addEventListener('click', this._onQuickBuyClick);
  }

  _onQuickBuyClick = e => {
    const button = e.target.closest(selectors.quickBuySize);
    if (!button || button.disabled) {
      return;
    }

    const variantId = button.dataset.variantId;
    if (!variantId) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const finalSale = this.el.dataset.finalSale === 'true';
    const preorder = this.el.dataset.preorder !== undefined;
    const preorderText = this.el.dataset.preorder;

    const additionalProperties = cartApi.addAdditionalProperties({
      finalSale,
      preorder,
      preorderText,
    });

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = getTranslation('products.adding');

    cartApi
      .add({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { ...additionalProperties },
          },
        ],
      })
      .then(response => {
        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }
        trackKlaviyoAddedToCart(readKlaviyoLearnqProduct(this.el));
        button.textContent = getTranslation('products.added');
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
        }, 1500);
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = originalText;
      });
  };

  _destroySwatchCarousel() {
    if (this._productSwatches) {
      this._productSwatches.destroy();
      this._productSwatches = null;
    }
  }

  _initSwatchCarousel() {
    this._destroySwatchCarousel();

    const container = this.el.querySelector(selectors.swatchesContainer);
    if (!container?.querySelector('[data-product-item-swatches-splide]')) {
      return;
    }

    this._productSwatches = new ProductSwatches(container);
  }

  /**
   * Swap product-card content when a swatch is selected.
   */
  _initSwatchSwap() {
    this.el.addEventListener('change', e => {
      const target = e.target;
      if (!target) {
        return;
      }

      if (
        target instanceof HTMLSelectElement &&
        (target.matches(selectors.cupSelect) ||
          target.matches(selectors.lengthSelect))
      ) {
        const handle = target.value;
        if (handle) {
          this._swapToHandle(handle, { swapSwatches: true });
        }
        return;
      }

      if (!(target instanceof HTMLInputElement)) {
        return;
      }

      if (!target.matches(selectors.swatchInput)) {
        return;
      }

      e.stopPropagation();

      const handle = target.dataset.swatchProductUrl;
      if (!handle) {
        return;
      }

      this._swapToHandle(handle);
    });
  }

  _swapToHandle(handle, { swapSwatches = false } = {}) {
    const cacheKey = this._getCacheKey(handle);
    const isCached = productCardCache.has(cacheKey);
    const swapToken = (this._activeSwapToken += 1);

    if (!isCached) {
      this.el.setAttribute('data-product-item-loading', 'true');
    }

    this._getCardData(handle)
      .then(cardData => {
        if (swapToken !== this._activeSwapToken) {
          return;
        }

        if (!cardData) {
          return;
        }

        this._swapCardContent(cardData, { swapSwatches });
      })
      .finally(() => {
        if (swapToken !== this._activeSwapToken) {
          return;
        }

        this.el.removeAttribute('data-product-item-loading');
      });
  }

  _getCardData(handle) {
    const cacheKey = this._getCacheKey(handle);
    const cached = productCardCache.get(cacheKey);
    if (cached) {
      return Promise.resolve(cached);
    }

    const root = window.Shopify?.routes?.root || '/';
    const sectionId = this._getFragmentSectionId();
    const url = `${root}products/${handle}?section_id=${sectionId}`;

    return fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const parsedHTML = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );

        const newCard = parsedHTML.querySelector('[data-product-item]');
        if (!newCard) {
          return null;
        }

        const newProductUrl = newCard.dataset.productUrl;
        if (!newProductUrl) {
          return null;
        }

        const cardData = this._extractCardData(newCard);
        productCardCache.set(cacheKey, cardData);
        return cardData;
      })
      .catch(() => null);
  }

  _extractCardData(newCard) {
    const imageLink = newCard.querySelector(selectors.imageLink);
    const imageContainer = imageLink?.parentElement;
    const titleLink = newCard.querySelector(selectors.titleLink);
    const price = newCard.querySelector(selectors.price);
    const badges = newCard.querySelector(selectors.badges);
    const color = newCard.querySelector(selectors.color);
    const options = newCard.querySelector(selectors.options);
    const form = newCard.querySelector(selectors.addToCartForm);
    const swatches = newCard.querySelector(selectors.swatchesContainer);
    const klaviyoScript = newCard.querySelector(
      'script[type="application/json"][data-klaviyo-learnq-product]',
    );

    return {
      url: newCard.dataset.productUrl,
      dataset: { ...newCard.dataset },
      imageContainerHtml: imageContainer ? imageContainer.innerHTML : '',
      title: titleLink ? titleLink.textContent : '',
      priceHtml: price ? price.innerHTML : '',
      badgesHtml: badges ? badges.innerHTML : '',
      color: color ? color.textContent : '',
      optionsOuterHtml: options ? options.outerHTML : '',
      formOuterHtml: form ? form.outerHTML : '',
      swatchesHtml: swatches ? swatches.outerHTML : '',
      klaviyoScriptOuterHtml: klaviyoScript ? klaviyoScript.outerHTML : '',
    };
  }

  _swapCardContent(cardData, { swapSwatches = false } = {}) {
    if (this._layout === 'horizontal-large') {
      this._swapHorizontalLargeContent(cardData, { swapSwatches });
      return;
    }

    if (this._layout === 'horizontal') {
      this._swapHorizontalContent(cardData);
      return;
    }

    this._swapDefaultCardContent(cardData);
  }

  _syncCardDataset(dataset) {
    if (!dataset) {
      return;
    }

    if ('finalSale' in dataset) {
      if (dataset.finalSale) {
        this.el.dataset.finalSale = dataset.finalSale;
      } else {
        delete this.el.dataset.finalSale;
      }
    }

    if ('preorder' in dataset) {
      if (dataset.preorder) {
        this.el.dataset.preorder = dataset.preorder;
      } else {
        delete this.el.dataset.preorder;
      }
    }

    if ('productItemFromPrice' in dataset) {
      this.el.dataset.productItemFromPrice = dataset.productItemFromPrice;
    }

    if (dataset.productItemSoldOut) {
      this.el.dataset.productItemSoldOut = dataset.productItemSoldOut;
    } else {
      delete this.el.dataset.productItemSoldOut;
    }
  }

  _applyBaseCardSwap({
    url,
    title,
    imageContainerHtml,
    priceHtml,
    badgesHtml,
    color,
    klaviyoScriptOuterHtml,
  }) {
    this.el.dataset.productUrl = url;

    if (klaviyoScriptOuterHtml) {
      const existing = this.el.querySelector(
        'script[type="application/json"][data-klaviyo-learnq-product]',
      );
      if (existing) {
        existing.outerHTML = klaviyoScriptOuterHtml;
      } else {
        this.el.insertAdjacentHTML('afterbegin', klaviyoScriptOuterHtml);
      }
    }

    const links = this.el.querySelectorAll(selectors.productLinks);
    links.forEach(link => {
      link.setAttribute('href', url);
    });

    const imageContainer = this.el.querySelector(
      selectors.imageLink,
    )?.parentElement;

    if (imageContainer && imageContainerHtml) {
      imageContainer.innerHTML = imageContainerHtml;
      reinitSwymActionButtons(this.el);
    }

    const titleLink = this.el.querySelector(selectors.titleLink);
    if (titleLink) {
      titleLink.setAttribute('href', url);
      titleLink.textContent = title || '';
    }

    const price = this.el.querySelector(selectors.price);
    if (price) {
      price.innerHTML = priceHtml || '';
    }

    const badges = this.el.querySelector(selectors.badges);
    if (badges) {
      badges.innerHTML = badgesHtml || '';
    }

    const colorEl = this.el.querySelector(selectors.color);
    if (colorEl && color != null) {
      colorEl.textContent = color;
    }
  }

  _swapOptionsContainer(optionsOuterHtml) {
    const existing = this.el.querySelector(selectors.options);
    const hasNewOptions = Boolean(optionsOuterHtml);

    if (existing && hasNewOptions) {
      existing.outerHTML = optionsOuterHtml;
      return;
    }

    if (existing && !hasNewOptions) {
      existing.remove();
      return;
    }

    if (!existing && hasNewOptions) {
      const titleRow = this.el.querySelector(selectors.price)?.parentElement;
      titleRow?.insertAdjacentHTML('afterend', optionsOuterHtml);
    }
  }

  _swapSwatchesContainer(swatchesOuterHtml) {
    this._destroySwatchCarousel();

    const existing = this.el.querySelector(selectors.swatchesContainer);
    const hasNewSwatches = Boolean(swatchesOuterHtml);

    if (existing && hasNewSwatches) {
      existing.outerHTML = swatchesOuterHtml;
      this._initSwatchCarousel();
      return;
    }

    if (existing && !hasNewSwatches) {
      existing.remove();
      return;
    }

    if (!existing && hasNewSwatches) {
      const colorEl = this.el.querySelector(selectors.color);
      colorEl?.insertAdjacentHTML('afterend', swatchesOuterHtml);
      this._initSwatchCarousel();
    }
  }

  _getSelectedSize() {
    const sizeSelect = this.el.querySelector(selectors.sizeSelect);
    return sizeSelect?.value || null;
  }

  _restoreSelectedSize(sizeValue) {
    const sizeSelect = this.el.querySelector(selectors.sizeSelect);
    if (!sizeSelect) {
      return false;
    }

    const matchingOption = sizeValue
      ? [...sizeSelect.options].find(option => option.value === sizeValue)
      : null;

    if (!matchingOption || matchingOption.disabled) {
      sizeSelect.value = '';
      sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return false;
    }

    sizeSelect.value = sizeValue;
    sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  _swapHorizontalLargeContent(
    { dataset, optionsOuterHtml, formOuterHtml, swatchesHtml, ...baseCardData },
    { swapSwatches = false } = {},
  ) {
    const preservedSize = this._getSelectedSize();

    this._syncCardDataset(dataset);
    this._applyBaseCardSwap(baseCardData);

    this._swapOptionsContainer(optionsOuterHtml);

    if (swapSwatches) {
      this._swapSwatchesContainer(swatchesHtml);
    }

    const form = this.el.querySelector(selectors.addToCartForm);
    if (form && formOuterHtml) {
      form.outerHTML = formOuterHtml;
      this._initAddToCart();
      this._restoreSelectedSize(preservedSize);
    }

    this.el.dispatchEvent(
      new CustomEvent('product-item:updated', { bubbles: true }),
    );
  }

  _swapHorizontalContent({ dataset, formOuterHtml, ...baseCardData }) {
    this._syncCardDataset(dataset);
    this._applyBaseCardSwap(baseCardData);

    const form = this.el.querySelector(selectors.addToCartForm);
    if (form && formOuterHtml) {
      form.outerHTML = formOuterHtml;
      this._initAddToCart();
    }

    this.el.dispatchEvent(
      new CustomEvent('product-item:updated', { bubbles: true }),
    );
  }

  _swapDefaultCardContent(cardData) {
    this._applyBaseCardSwap(cardData);
  }
}
